import React, { useState } from 'react';
import { Bookmark, Check, ExternalLink, Minus, Plus } from 'lucide-react';
import { motion } from 'framer-motion';
import { formatPrice, getPricing, resolvePrice, type ProductPricing } from '../data/products';
import { buildShopifyProductUrl } from '../data/contact';
import type { ProductCopy } from '../types/i18n';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
interface ProductCardProps {
  product: ProductCopy;
  index: number;
}
export function ProductCard({
  product,
  index
}: ProductCardProps) {
  const {
    t,
    language
  } = useLanguage();
  const {
    addItem,
    saveForLater,
    isSaved
  } = useCart();
  const pricing = getPricing(product.id) as ProductPricing | undefined;
  const options = pricing?.options;
  const [optionId, setOptionId] = useState(options?.[0]?.id);
  const [quantity, setQuantity] = useState(1);
  const [isAdded, setIsAdded] = useState(false);
  const selectedOption = options?.find(option => option.id === optionId);
  const image = selectedOption?.image ?? pricing?.image;
  const unitPrice = resolvePrice(product.id, optionId);
  const saved = isSaved(product.id, optionId);
  const handleAdd = () => {
    addItem(product.id, optionId, quantity);
    setIsAdded(true);
    window.setTimeout(() => setIsAdded(false), 1600);
  };
  return <motion.article initial={{
    opacity: 0,
    y: 32
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-80px'
  }} transition={{
    duration: 0.6,
    delay: Math.min(index, 3) * 0.08
  }} className="flex flex-col border border-caramel/20 bg-creme-dark" data-id="element-210" data-mp="3810jnwvrlrwn">
      <div className="overflow-hidden bg-creme" data-id="element-211" data-mp="3vngrszjy6v6a">
        <img src={image} alt={product.name} className="aspect-[4/3] w-full object-cover transition-transform duration-500 hover:scale-[1.03]" data-id="element-212" data-mp="3qq72rl0h0liz" />
      </div>

      <div className="flex flex-1 flex-col p-6" data-id="element-213" data-mp="2fb1nzin2ewj0">
        <span className="mb-2 font-sans text-[10px] uppercase tracking-[0.18em] text-or" data-id="element-214" data-mp="2f972o37vt2ee">
          {product.subtitle}
        </span>
        <h3 className="mb-3 font-serif text-xl text-cacao-900" data-id="element-215" data-mp="2gq4g4mgyrg16">
          {product.name}
        </h3>

        {pricing && <p className="mb-4 font-sans" data-id="element-216" data-mp="27rsgjuy4d7wl">
            <span className="font-serif text-xl text-caramel" data-id="element-217" data-mp="27qr5vrl0mnfe">
              {formatPrice(unitPrice)}
            </span>{' '}
            <span className="text-xs font-light text-cacao-700" data-id="element-218" data-mp="2cs6qjija6rx9">
              ·{' '}
              {selectedOption ? selectedOption.label[language] : pricing.unit[language]}
            </span>
          </p>}

        <p className="mb-5 flex-1 font-sans text-sm font-light leading-relaxed text-cacao-700" data-id="element-219" data-mp="1p1ssnbe0lyur">
          {product.description}
        </p>

        <ul className="mb-6 flex flex-wrap gap-2" data-id="element-220" data-mp="1t0a4g88drxii">
          {product.tags.map(tag => <li key={tag} className="border border-caramel/40 px-3 py-1 font-sans text-[9px] uppercase tracking-[0.14em] text-cacao-800" data-id="element-221" data-mp="1f4ohs7pmdpt9">
              {tag}
            </li>)}
        </ul>

        {options && options.length > 0 && <fieldset className="mb-5" data-id="element-222" data-mp="vicxnbd7mu41">
            <legend className="mb-3 font-sans text-[10px] uppercase tracking-[0.16em] text-cacao-800" data-id="element-223" data-mp="1ae2gx1r0gt52">
              {t.products.chooseOption}
            </legend>
            <div className="flex flex-wrap gap-2" data-id="element-224" data-mp="15dobzskphzb9">
              {options.map(option => {
            const active = option.id === optionId;
            return <button key={option.id} type="button" aria-pressed={active} onClick={() => setOptionId(option.id)} className={`border px-3 py-2 font-sans text-[10px] uppercase tracking-[0.12em] transition-colors duration-200 ${active ? 'border-cacao-900 bg-cacao-900 text-creme' : 'border-caramel/40 text-cacao-800 hover:border-caramel'}`} data-id="element-225" data-mp="1eew7y3zr2o26">
                    {option.label[language]}
                  </button>;
          })}
            </div>
          </fieldset>}

        <div className="mb-4 flex items-center justify-between" data-id="element-226" data-mp="5840gk8y3rna">
          <span className="font-sans text-[10px] uppercase tracking-[0.16em] text-cacao-800" data-id="element-227" data-mp="a6dw0vyl0u9f">
            {t.products.quantity}
          </span>
          <div className="flex items-center border border-caramel/40" data-id="element-228" data-mp="38pch50rxslgf">
            <button type="button" onClick={() => setQuantity(value => Math.max(1, value - 1))} aria-label={`${t.products.quantity} -`} className="px-3 py-2 text-cacao-800 transition-colors hover:text-caramel" data-id="element-229" data-mp="3dnnrdcm58xz6">
              <Minus size={13} strokeWidth={2} data-id="element-230" data-mp="xvzwdmqoxfj0" />
            </button>
            <span className="min-w-8 text-center font-sans text-sm text-cacao-900" data-id="element-231" data-mp="xxt1uypkhtpf">
              {quantity}
            </span>
            <button type="button" onClick={() => setQuantity(value => value + 1)} aria-label={`${t.products.quantity} +`} className="px-3 py-2 text-cacao-800 transition-colors hover:text-caramel" data-id="element-232" data-mp="2mfm55kv27qsw">
              <Plus size={13} strokeWidth={2} data-id="element-233" data-mp="2dkfzclvsh8qd" />
            </button>
          </div>
        </div>

        <button type="button" onClick={handleAdd} className={`mb-3 flex w-full items-center justify-center gap-2 py-3 font-sans text-[11px] uppercase tracking-[0.16em] transition-colors duration-300 ${isAdded ? 'bg-caramel text-creme' : 'bg-cacao-900 text-creme hover:bg-caramel'}`} data-id="element-234" data-mp="28lv7b9qoxvd0">
          {isAdded ? <Check size={15} strokeWidth={2} aria-hidden="true" data-id="element-235" data-mp="1pr6hxvff9tiy" /> : <Plus size={15} strokeWidth={2} aria-hidden="true" data-id="element-236" data-mp="3p165y644k2ki" />}
          {isAdded ? t.products.added : t.products.addToCart}
        </button>

        <button type="button" onClick={() => saveForLater(product.id, optionId)} aria-pressed={saved} className={`mb-3 flex w-full items-center justify-center gap-2 border py-2.5 font-sans text-[10px] uppercase tracking-[0.14em] transition-colors duration-300 ${saved ? 'border-caramel bg-caramel/10 text-caramel' : 'border-caramel/40 text-cacao-800 hover:border-caramel hover:text-caramel'}`} data-id="element-237" data-mp="71ttd6gz2v09">
          <Bookmark size={13} strokeWidth={1.75} className={saved ? 'fill-current' : ''} aria-hidden="true" data-id="element-238" data-mp="urxxdey2owrs" />
          {saved ? t.products.savedLabel : t.products.saveForLater}
        </button>

        <a href={buildShopifyProductUrl(pricing?.handle, language)} target="_blank" rel="noopener noreferrer" className="flex w-full items-center justify-center gap-2 py-2 font-sans text-[10px] uppercase tracking-[0.14em] text-cacao-700 transition-colors duration-300 hover:text-caramel" data-id="element-239" data-mp="lt2r62v13moh">
          <ExternalLink size={12} strokeWidth={1.75} aria-hidden="true" data-id="element-240" data-mp="2tz4mmz22dprg" />
          {t.products.orderNow}
        </a>
      </div>
    </motion.article>;
}